import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { toast } from "react-toastify";
import { IImageInfo, IResponseProjectData } from "@types";
import { tempFileState } from "../../recoil";
import ServerFileModal from "../ServerFileModal";
import { deleteImageLabels, getProject, patchDatasetImages } from "../../services/projectService";
import { editDatasetFile } from "../../services/datasetService";

interface DatasetSettingProps {
  id?: string;
  projectData?: IResponseProjectData;
}

const toLabel = (label: IImageInfo["label"]) => (Array.isArray(label) ? label[0] : label) || "unlabeled";

// 프로젝트 생성 때와 같은 ServerFileModal UI로 기존 데이터셋 편집
export default function DatasetSetting({ id, projectData }: DatasetSettingProps) {
  const [tempFiles, setTempFiles] = useRecoilState(tempFileState);
  const [baseImages, setBaseImages] = useState<IImageInfo[]>(projectData?.images ?? []); // 마지막으로 저장된 데이터셋

  const resetTempFiles = (images: IImageInfo[]) =>
    setTempFiles(images.map((image, index) => ({ id: index + 1, path: image.path, label: toLabel(image.label) })));

  useEffect(() => {
    const images = projectData?.images ?? [];
    setBaseImages(images);
    resetTempFiles(images);
    return () => setTempFiles([]); // 다른 페이지(프로젝트 생성 등)로 목록이 새지 않도록 초기화
  }, [projectData]);

  const handleApply = async () => {
    const basePaths = new Set(baseImages.map((image) => image.path));
    const currentPaths = new Set(tempFiles.map((file) => file.path));
    const baseLabels = new Map(baseImages.map((image) => [image.path, toLabel(image.label)]));

    const addedFiles = tempFiles.filter((file) => !basePaths.has(file.path));
    const removedImageIds = baseImages
      .filter((image) => !currentPaths.has(image.path))
      .map((image) => String(image.id));
    const isLabelChanged = tempFiles.some(
      (file) => basePaths.has(file.path) && baseLabels.get(file.path) !== file.label,
    );

    if (addedFiles.length === 0 && removedImageIds.length === 0 && !isLabelChanged) {
      toast.info("변경된 내용이 없습니다.");
      return;
    }
    const confirm = window.confirm(
      "데이터셋 변경 시 기존 학습된 데이터와의 연관관계가 끊어집니다. \n변경하시겠습니까?",
    );
    if (!confirm) return;

    try {
      const classList = projectData?.class || [];

      const groupedFiles = tempFiles.reduce(
        (acc, file) => {
          const label = file.label || "unlabeled";
          const labelKey = classList.includes(label) ? label : "unlabeled";
          (acc[labelKey] ??= []).push(file.path);
          return acc;
        },
        {} as Record<string, string[]>,
      );
      await editDatasetFile(Number(projectData?.dataSet_id), classList, groupedFiles);

      if (removedImageIds.length > 0) {
        await deleteImageLabels(removedImageIds);
      }
      if (addedFiles.length > 0 || removedImageIds.length > 0) {
        await patchDatasetImages(
          Number(id),
          addedFiles.map((file) => file.path),
        );
      }
      toast.success("데이터셋 업데이트가 완료되었습니다.");

      // 저장된 상태를 새 기준으로 갱신 (다음 적용 시 diff 기준)
      const response = await getProject(Number(id));
      setBaseImages(response.project.images);
      resetTempFiles(response.project.images);
    } catch (error) {
      toast.error("적용 중 오류가 발생했습니다.");
      console.error("적용 에러", error);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid var(--dk-border, #e5e7eb)",
        borderRadius: "12px",
        background: "var(--dk-surface, #fff)",
        overflow: "hidden",
      }}
    >
      <ServerFileModal
        onClick={() => resetTempFiles(baseImages)} // 취소 → 저장된 상태로 되돌림
        onConfirm={handleApply}
        contentSx={{ width: "100%", height: "700px" }}
      />
    </Box>
  );
}
